import { useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'

import { ApiError, errorMessage } from '@/shared/api/httpClient'
import { roomsApi } from '@/shared/api/roomsApi'
import { useAsync } from '@/shared/api/useAsync'
import { Corners } from '@/shared/ui/Blueprint'
import { Wordmark } from '@/shared/ui/Wordmark'
import { mcpApi } from '@/features/mcp/mcpApi'

const check = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
)

function Card({ width = 520, center = false, children }: { width?: number; center?: boolean; children: ReactNode }) {
  return (
    <div style={{ flex: 1, display: 'grid', placeItems: 'center', padding: 'clamp(24px,8vw,60px) clamp(16px,5vw,24px)' }}>
      <div className="dialog blueprint" style={{ background: 'var(--color-bg)', width: `min(${width}px,100%)`, ...(center && { alignItems: 'center', textAlign: 'center' }) }}>
        <Corners />
        {children}
      </div>
    </div>
  )
}

export function ConsentPage() {
  const req = useSearchParams()[0].get('req') ?? ''
  const { data, error, loading } = useAsync(async () => {
    if (!req) throw new ApiError(400, { code: 'ValidationError', message: 'Pedido de autorização inválido: falta o parâmetro req.' })
    const [info, rooms] = await Promise.all([mcpApi.consent(req), roomsApi.list()])
    return { info, rooms }
  }, [req])

  const [canExecute, setCanExecute] = useState<boolean | null>(null) // null = padrão pelos escopos pedidos
  const [roomId, setRoomId] = useState('') // '' = toda a casa
  const [returning, setReturning] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  if (loading) return <p className="page-state">Carregando…</p>
  if (!data) {
    return (
      <Card width={440}>
        <div className="dialog-title">Não foi possível abrir o consentimento</div>
        <div className="dialog-body">{error}</div>
      </Card>
    )
  }

  const { info, rooms } = data
  const executeAllowed = info.scopes.includes('devices.execute')
  const exec = canExecute ?? executeAllowed

  if (returning) {
    return (
      <Card width={420} center>
        <span className="pulse" style={{ width: 11, height: 11 }} />
        <div className="dialog-title" style={{ marginTop: 8 }}>Voltando para o {info.client_name}…</div>
        <div className="dialog-body">Trocando o código de autorização pelo token. Não feche esta janela.</div>
      </Card>
    )
  }

  async function decide(allow: boolean) {
    setSubmitError(null)
    try {
      const { redirect_to } = await mcpApi.decide({ req, allow, can_execute: allow && exec, rooms: roomId ? [Number(roomId)] : [] })
      setReturning(true)
      window.location.assign(redirect_to)
    } catch (err) {
      setSubmitError(errorMessage(err))
    }
  }

  const permissions = [
    'Ver seus dispositivos, cômodos e o estado de cada um',
    exec ? 'Executar ações: ligar, desligar, brilho, temperatura' : 'Não executa nenhuma ação — apenas leitura',
    'Nada de acesso a credenciais dos fabricantes ou à sua conta Lumo',
  ]

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Wordmark style={{ fontSize: 16 }} />
        <span className="text-muted" style={{ fontSize: 12 }}>be.lumo.connectakit.com.br/oauth/authorize</span>
      </div>
      <div className="dialog-title">Dar acesso à sua casa para o {info.client_name}?</div>
      <div className="dialog-body">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
          {permissions.map((p) => (
            <div key={p} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--color-accent-700)', flex: 'none', marginTop: 2 }}>{check}</span>
              <span style={{ fontSize: 14 }}>{p}</span>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div className="field" style={{ flex: '1 1 170px' }}>
            <label htmlFor="consent-room">Escopo de cômodos</label>
            <select id="consent-room" className="input" value={roomId} onChange={(e) => setRoomId(e.target.value)}>
              <option value="">Todos</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ flex: '1 1 200px' }}>
            <label>Permissão</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className={`btn ${exec ? 'btn-secondary' : 'btn-primary'}`} onClick={() => setCanExecute(false)} style={{ flex: 1 }}>
                Só ler
              </button>
              {executeAllowed && (
                <button type="button" className={`btn ${exec ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setCanExecute(true)} style={{ flex: 1 }}>
                  Ler e agir
                </button>
              )}
            </div>
          </div>
        </div>
        <p className="text-muted" style={{ fontSize: 12, margin: '14px 0 0' }}>
          Depois de decidir, você volta para <span className="mono">{info.redirect_host}</span>.
        </p>
        {submitError && <p className="form-error" style={{ marginTop: 10 }}>{submitError}</p>}
      </div>
      <div className="dialog-actions">
        <button type="button" className="btn btn-secondary" onClick={() => decide(false)}>
          Cancelar
        </button>
        <button type="button" className="btn btn-primary blueprint" onClick={() => decide(true)} style={{ padding: '9px 16px' }}>
          <Corners />
          Autorizar
        </button>
      </div>
    </Card>
  )
}
